import { initMelchiorSlider } from "../../common/init-melchior-slider";
import { initSwiper } from "../../common/init-swiper";
import { loadScript } from "../../common/load-script";
import { debounced } from "../../utils";
import { initOffuscation } from "../link-offuscation";

class GiftPackageSearch {
  constructor() {
    this.wrapper = document.getElementById("gift-package-search");
    if (!this.wrapper) return;

    this.resultsContainer = this.wrapper.querySelector('#packages-results-container');
    if (!this.resultsContainer) return;

    this.countResultsElts = this.wrapper.querySelectorAll('.count-results');

    // Get taxonomy ID filter if present (permanent filter for taxonomy pages)
    this.taxonomyIdFilter = this.wrapper.getAttribute('data-taxonomy-id-filter') || null;
    this.additionalTaxonomyIdFilter = this.wrapper.getAttribute('data-additional-taxonomy-id-filter') || null;

    // Initialize filters object to store all filter values
    this.filters = {
      location: {
        countries: [],
        regions: [],
        cities: [],
        lat: null,
        lng: null
      },
      themes: [],
      occasions: [],
      activities: [],
      persons: [],
      price: {
        min: null,
        max: null
      },
      sort: null,
      taxonomyId: this.taxonomyIdFilter ? parseInt(this.taxonomyIdFilter) : null,
      additionalTaxonomyId: this.additionalTaxonomyIdFilter ? parseInt(this.additionalTaxonomyIdFilter) : null
    };

    // Store previous filters to prevent duplicate searches
    this.previousFilters = null;

    // Track the current page of experiences loaded (for preserving on filter changes)
    this.currentExperiencesPage = 1;

    // Initialize filter configurations
    this.filterConfigs = this.setFilterConfigs();

    // Create debounced search function
    this.debouncedPerformSearch = debounced(500, this.performSearch.bind(this));

    this.init();
  }

  init() {
    this.initPriceSlider();
    this.initLocationAutocomplete();
    this.initAroundMeButton();
    this.initLocationSeeMore();
    this.initCountryFiltering();
    this.initRegionFiltering();
    this.initFilterEventListeners();
    this.initDropdownEventListeners();
    this.initSortingEventListeners();
    this.initMobileDrawer();
    this.initShowMoreExperiences();

    // Initialize price filters from URL parameters after slider is initialized
    // Use a small delay to ensure slider elements are ready
    setTimeout(() => {
      this.initPriceFromUrlParams();
      // Initialize mobile button count
      this.updateMobileFiltersButton();
    }, 50);

    // Initialize results count
    // this.updateResultsCount();
  }

  /**
   * Set filter configurations
   * @returns {Object} - Filter configurations object
   */
  setFilterConfigs() {
    const configs = {};
    const filterTypes = ['location', 'theme', 'occasion', 'activity', 'persons', 'price'];

    filterTypes.forEach(filterType => {
      const button = this.wrapper.querySelector(`[data-filter-button="${filterType}"]`);
      const initialText = button ? button.getAttribute('data-initial-text') || filterType : filterType;

      configs[filterType] = {
        selector: `[data-filter="${filterType}"]`,
        buttonSelector: `[data-filter-button="${filterType}"]`,
        clearSelector: `[data-filter-clear="${filterType}"]`,
        applySelector: `[data-filter-apply="${filterType}"]`,
        initialText: initialText
      };
    });

    return configs;
  }

  /**
   * Initialize price slider functionality
   */
  initPriceSlider() {
    // init input type range logic (min/max)
    initMelchiorSlider();

    this.priceRangeContainer = this.wrapper.querySelector('#price-slider-wrapper .range_container');
    this.minPriceInput = this.wrapper.querySelector('#price-slider-wrapper .fromInput');
    this.maxPriceInput = this.wrapper.querySelector('#price-slider-wrapper .toInput');
  }

  /**
   * Initialize Google Places autocomplete for location
   */
  initLocationAutocomplete() {
    this.locationAutocompleteInput = this.wrapper.querySelector('#location-autocomplete');
    this.locationLatInput = this.wrapper.querySelector('#location-lat');
    this.locationLngInput = this.wrapper.querySelector('#location-lng');
    this.aroundMeChip = this.wrapper.querySelector('#location-around-me-chip');

    if (!this.locationAutocompleteInput) {
      return;
    }

    if (window.google && window.google.maps) {
      this.bindLocationAutocomplete();
      return;
    }

    // Load Google Maps only when the user starts using the location input
    this.locationAutocompleteInput.addEventListener('focus', () => {
      loadScript(this.locationAutocompleteInput.dataset.gmapsSrc)
        .then(() => this.bindLocationAutocomplete());
    }, { once: true });
  }

  /**
   * Bind Google Places autocomplete to the location input
   */
  bindLocationAutocomplete() {
    const autocomplete = new google.maps.places.Autocomplete(this.locationAutocompleteInput);

    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      const place = autocomplete.getPlace();

      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        this.locationLatInput.value = lat;
        this.locationLngInput.value = lng;

        this.filters.location.lat = lat;
        this.filters.location.lng = lng;

        // Hide the around me chip when autocomplete is used
        if (this.aroundMeChip) {
          this.aroundMeChip.classList.add('hidden');
          this.aroundMeChip.classList.remove('inline-flex');
        }

        // Show the around me button
        if (this.aroundMeButton) {
          this.aroundMeButton.classList.remove('hidden');
        }

        // Update filter button label to show active state
        this.updateFilterButtonLabel('location');

        // Close the location filter dropdown
        this.closeFilterDropdown('location');

        // Trigger search immediately
        this.triggerSearch();
      }
    });
  }

  /**
   * Initialize "Around me" button for geolocation
   */
  initAroundMeButton() {
    this.aroundMeButton = this.wrapper.querySelector('#location-around-me');
    this.aroundMeChip = this.wrapper.querySelector('#location-around-me-chip');

    if (!this.aroundMeButton) {
      return;
    }

    this.aroundMeButton.addEventListener('click', () => {
      if (!navigator.geolocation) {
        alert('Geolocation is not supported by this browser.');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          // Set the coordinates
          this.locationLatInput.value = lat;
          this.locationLngInput.value = lng;
          this.filters.location.lat = lat;
          this.filters.location.lng = lng;

          // Clear the autocomplete input
          if (this.locationAutocompleteInput) {
            this.locationAutocompleteInput.value = '';
          }

          // Show the around me chip
          if (this.aroundMeChip) {
            this.aroundMeChip.classList.remove('hidden');
            this.aroundMeChip.classList.add('inline-flex');
          }

          // Hide the around me button
          this.aroundMeButton.classList.add('hidden');

          // Update filter button label to show active state
          this.updateFilterButtonLabel('location');

          // Close the location filter dropdown
          this.closeFilterDropdown('location');

          // Trigger search immediately
          this.triggerSearch();
        }
      );
    });
  }

  /**
   * Initialize "See more" functionality for location lists
   */
  initLocationSeeMore() {
    const seeMoreButtons = this.wrapper.querySelectorAll('[data-see-more]');

    seeMoreButtons.forEach(button => {
      button.addEventListener('click', () => {
        const locationType = button.getAttribute('data-see-more');
        const locationList = this.wrapper.querySelector(`[data-location-list="${locationType}"]`);

        if (!locationList) return;

        // Only consider items that are not filtered by country or region
        const visibleItems = locationList.querySelectorAll('.location-list-item:not(.filtered-by-country):not(.filtered-by-region)');
        const hiddenItems = locationList.querySelectorAll('.location-list-item.hidden:not(.filtered-by-country):not(.filtered-by-region)');
        const isExpanded = hiddenItems.length === 0;

        if (isExpanded) {
          // Hide items beyond index 7 (first 8 items) but only for non-filtered items
          visibleItems.forEach((item, index) => {
            if (index >= 8) {
              item.classList.add('hidden');
            }
          });
          button.textContent = button.getAttribute('data-text-more');
        } else {
          // Show all hidden items that are not filtered by country or region
          hiddenItems.forEach(item => {
            item.classList.remove('hidden');
          });
          button.textContent = button.getAttribute('data-text-less');
        }
      });
    });
  }

  /**
   * Initialize country checkbox filtering for regions and cities
   */
  initCountryFiltering() {
    const locationFilter = this.wrapper.querySelector('[data-filter="location"]');
    if (!locationFilter) return;

    const countryCheckboxes = locationFilter.querySelectorAll('input[name="countries_ids[]"]');

    countryCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        this.filterLocationsByCountries();
      });
    });
  }

  /**
   * Initialize region checkbox filtering for cities
   */
  initRegionFiltering() {
    const locationFilter = this.wrapper.querySelector('[data-filter="location"]');
    if (!locationFilter) return;

    const regionCheckboxes = locationFilter.querySelectorAll('input[name="regions_ids[]"]');

    regionCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        this.filterCitiesByRegions();
      });
    });
  }

  /**
   * Toggle visibility of "See more" buttons based on filtering state
   * @param {string} locationType - Type of location (regions, cities)
   * @param {boolean} hasActiveFiltering - Whether filtering is currently active
   */
  toggleSeeMoreButton(locationType, hasActiveFiltering) {
    const seeMoreButton = this.wrapper.querySelector(`[data-see-more="${locationType}"]`);
    if (!seeMoreButton) return;

    if (hasActiveFiltering) {
      seeMoreButton.classList.add('hidden');
    } else {
      seeMoreButton.classList.remove('hidden');
    }
  }

  /**
   * Filter regions and cities based on selected countries
   */
  filterLocationsByCountries() {
    const locationFilter = this.wrapper.querySelector('[data-filter="location"]');
    if (!locationFilter) return;

    // Get all checked country IDs
    const checkedCountries = Array.from(
      locationFilter.querySelectorAll('input[name="countries_ids[]"]:checked')
    ).map(cb => cb.value);

    const regionsList = locationFilter.querySelector('[data-location-list="regions"]');
    const citiesList = locationFilter.querySelector('[data-location-list="cities"]');

    // If no countries are selected, show all regions and cities
    if (checkedCountries.length === 0) {
      if (regionsList) {
        const allRegionItems = regionsList.querySelectorAll('.location-list-item');
        allRegionItems.forEach((item, index) => {
          item.classList.remove('filtered-by-country');
          // Respect the "see more" functionality (first 8 visible)
          if (index < 8) {
            item.classList.remove('hidden');
          }
        });
      }

      if (citiesList) {
        const allCityItems = citiesList.querySelectorAll('.location-list-item');
        allCityItems.forEach((item, index) => {
          item.classList.remove('filtered-by-country');
          // Respect the "see more" functionality (first 8 visible)
          if (index < 8) {
            item.classList.remove('hidden');
          }
        });
      }

      // Show "See more" buttons when no country filtering is active
      this.toggleSeeMoreButton('regions', false);
      this.toggleSeeMoreButton('cities', false);

      // After country filtering is cleared, apply region filtering if any regions are selected
      this.filterCitiesByRegions();
      return;
    }

    // Filter regions
    if (regionsList) {
      const regionItems = regionsList.querySelectorAll('.location-list-item');
      regionItems.forEach(item => {
        const regionCountryId = item.getAttribute('data-country-id');

        if (!checkedCountries.includes(regionCountryId)) {
          // Hide and uncheck regions not in selected countries
          item.classList.add('hidden');
          item.classList.add('filtered-by-country');
          const checkbox = item.querySelector('input[type="checkbox"]');
          if (checkbox && checkbox.checked) {
            checkbox.checked = false;
          }
        } else {
          // Show regions from selected countries
          item.classList.remove('hidden');
          item.classList.remove('filtered-by-country');
        }
      });
    }

    // Filter cities by country
    if (citiesList) {
      const cityItems = citiesList.querySelectorAll('.location-list-item');
      cityItems.forEach(item => {
        const cityCountryId = item.getAttribute('data-country-id');

        if (!checkedCountries.includes(cityCountryId)) {
          // Hide and uncheck cities not in selected countries
          item.classList.add('hidden');
          item.classList.add('filtered-by-country');
          const checkbox = item.querySelector('input[type="checkbox"]');
          if (checkbox && checkbox.checked) {
            checkbox.checked = false;
          }
        } else {
          // Show cities from selected countries
          item.classList.remove('hidden');
          item.classList.remove('filtered-by-country');
        }
      });
    }

    // Hide "See more" buttons when country filtering is active
    this.toggleSeeMoreButton('regions', true);
    this.toggleSeeMoreButton('cities', true);

    // After country filtering, also apply region filtering
    this.filterCitiesByRegions();
  }

  /**
   * Filter cities based on selected regions
   */
  filterCitiesByRegions() {
    const locationFilter = this.wrapper.querySelector('[data-filter="location"]');
    if (!locationFilter) return;

    // Get all checked region IDs (but only from visible regions, not filtered by country)
    const checkedRegions = Array.from(
      locationFilter.querySelectorAll('input[name="regions_ids[]"]:checked')
    ).filter(cb => {
      const regionItem = cb.closest('.location-list-item');
      return regionItem && !regionItem.classList.contains('filtered-by-country');
    }).map(cb => cb.value);

    const citiesList = locationFilter.querySelector('[data-location-list="cities"]');
    if (!citiesList) return;

    const cityItems = citiesList.querySelectorAll('.location-list-item');

    // Check if countries are selected (to avoid overriding their button state)
    const checkedCountries = Array.from(
      locationFilter.querySelectorAll('input[name="countries_ids[]"]:checked')
    );
    const hasCountryFiltering = checkedCountries.length > 0;

    // If no regions are selected, show all cities (but respect country filtering)
    if (checkedRegions.length === 0) {
      cityItems.forEach((item, index) => {
        // Only modify cities that aren't filtered by country
        if (!item.classList.contains('filtered-by-country')) {
          item.classList.remove('filtered-by-region');
          // Respect the "see more" functionality (first 8 visible)
          if (item.classList.contains('hidden') && index < 8) {
            item.classList.remove('hidden');
          }
        }
      });

      // Only show "See more" button if no country filtering is active
      if (!hasCountryFiltering) {
        this.toggleSeeMoreButton('cities', false);
      }
      return;
    }

    // Filter cities by selected regions
    cityItems.forEach(item => {
      // Skip cities that are already filtered by country
      if (item.classList.contains('filtered-by-country')) {
        return;
      }

      const cityRegionId = item.getAttribute('data-region-id');

      if (!checkedRegions.includes(cityRegionId)) {
        // Hide and uncheck cities not in selected regions
        item.classList.add('hidden');
        item.classList.add('filtered-by-region');
        const checkbox = item.querySelector('input[type="checkbox"]');
        if (checkbox && checkbox.checked) {
          checkbox.checked = false;
        }
      } else {
        // Show cities from selected regions
        item.classList.remove('hidden');
        item.classList.remove('filtered-by-region');
      }
    });

    // Hide "See more" button for cities when region filtering is active
    this.toggleSeeMoreButton('cities', true);
  }

  /**
   * Initialize price filters from URL parameters
   */
  initPriceFromUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const priceMin = urlParams.get('price_min');
    const priceMax = urlParams.get('price_max');
    let hasUrlPriceParams = false;

    // Update internal filter state if URL params exist
    if (priceMin !== null) {
      const minValue = parseInt(priceMin);
      if (!isNaN(minValue) && minValue >= 0) {
        this.filters.price.min = minValue;
        hasUrlPriceParams = true;
      }
    }

    if (priceMax !== null) {
      const maxValue = parseInt(priceMax);
      if (!isNaN(maxValue) && maxValue >= 0) {
        this.filters.price.max = maxValue;
        hasUrlPriceParams = true;
      }
    }

    // Update filter button label and trigger search if any price filters are active
    if (hasUrlPriceParams) {
      this.updateFilterButtonLabel('price');

      // Trigger search to update results based on URL parameters
      this.triggerSearch();
    }
  }

  /**
   * Initialize event listeners for all filter dropdowns
   */
  initFilterEventListeners() {
    Object.keys(this.filterConfigs).forEach(filterType => {
      this.initFilterTypeListeners(filterType);
    });
  }

  /**
   * Initialize listeners for a specific filter type
   * @param {string} filterType - The type of filter (location, theme, etc.)
   */
  initFilterTypeListeners(filterType) {
    const config = this.filterConfigs[filterType];

    // Apply button listener
    const applyBtn = this.wrapper.querySelector(config.applySelector);
    if (applyBtn) {
      applyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleFilterApply(filterType);
      });
    }

    // Clear button listener
    const clearBtn = this.wrapper.querySelector(config.clearSelector);
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleFilterClear(filterType);
      });
    }

    // Input change listeners for the specific filter type
    this.initFilterInputListeners(filterType);
  }

  /**
   * Initialize input change listeners for filter inputs
   * @param {string} filterType - The type of filter
   */
  initFilterInputListeners(filterType) {
    const config = this.filterConfigs[filterType];
    const filterContainer = this.wrapper.querySelector(config.selector);

    if (!filterContainer) return;

    if (filterType === 'price') {
      // Handle price slider inputs
      if (this.minPriceInput) {
        this.minPriceInput.addEventListener('input', () => {
          this.updatePriceFilter();
        });
      }
      if (this.maxPriceInput) {
        this.maxPriceInput.addEventListener('input', () => {
          this.updatePriceFilter();
        });
      }
    } else {
      // Handle checkbox inputs for other filter types
      const checkboxes = filterContainer.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          this.updateFilterFromInputs(filterType);
        });
      });
    }
  }

  /**
   * Update price filter values from slider inputs
   */
  updatePriceFilter() {
    if (this.minPriceInput && this.maxPriceInput) {
      this.filters.price.min = parseInt(this.minPriceInput.value) || null;
      this.filters.price.max = parseInt(this.maxPriceInput.value) || null;
    }
  }

  /**
   * Update filter values from checkbox inputs
   * @param {string} filterType - The type of filter
   */
  updateFilterFromInputs(filterType) {
    const config = this.filterConfigs[filterType];
    const filterContainer = this.wrapper.querySelector(config.selector);

    if (!filterContainer) return;

    const checkboxes = filterContainer.querySelectorAll('input[type="checkbox"]:checked');

    switch (filterType) {
      case 'location':
        // Reset location filters
        this.filters.location = {
          countries: [],
          regions: [],
          cities: [],
          lat: null,
          lng: null
        };

        // Get lat/lng from autocomplete if present
        if (this.locationLatInput && this.locationLngInput) {
          const lat = parseFloat(this.locationLatInput.value);
          const lng = parseFloat(this.locationLngInput.value);

          if (!isNaN(lat) && !isNaN(lng)) {
            this.filters.location.lat = lat;
            this.filters.location.lng = lng;
          }
        }

        checkboxes.forEach(checkbox => {
          const value = checkbox.value;
          const name = checkbox.name;

          if (name === 'countries_ids[]') {
            this.filters.location.countries.push(parseInt(value));
          } else if (name === 'regions_ids[]') {
            this.filters.location.regions.push(parseInt(value));
          } else if (name === 'cities_ids[]') {
            this.filters.location.cities.push(parseInt(value));
          }
        });
        break;

      case 'theme':
        this.filters.themes = Array.from(checkboxes).map(cb => parseInt(cb.value));
        break;

      case 'occasion':
        this.filters.occasions = Array.from(checkboxes).map(cb => parseInt(cb.value));
        break;

      case 'activity':
        this.filters.activities = Array.from(checkboxes).map(cb => parseInt(cb.value));
        break;

      case 'persons':
        this.filters.persons = Array.from(checkboxes).map(cb => parseInt(cb.value));
        break;
    }
  }

  /**
   * Handle apply button click for a filter
   * @param {string} filterType - The type of filter
   */
  handleFilterApply(filterType) {
    // Update filter values from inputs
    if (filterType === 'price') {
      this.updatePriceFilter();
    } else {
      this.updateFilterFromInputs(filterType);
    }

    // Update button label
    this.updateFilterButtonLabel(filterType);

    // Update mobile filters button
    this.updateMobileFiltersButton();

    // Close dropdown
    this.closeFilterDropdown(filterType);

    // Trigger search
    this.triggerSearch();
  }

  /**
   * Handle clear button click for a filter
   * @param {string} filterType - The type of filter
   */
  handleFilterClear(filterType) {
    // Clear filter values
    this.clearFilterValues(filterType);

    // Clear input states
    this.clearFilterInputs(filterType);

    // Update button label
    this.updateFilterButtonLabel(filterType);

    // Update mobile filters button
    this.updateMobileFiltersButton();

    // Close dropdown
    this.closeFilterDropdown(filterType);

    // Trigger search
    this.triggerSearch();
  }

  /**
   * Clear filter values for a specific filter type
   * @param {string} filterType - The type of filter
   */
  clearFilterValues(filterType) {
    switch (filterType) {
      case 'location':
        this.filters.location = {
          countries: [],
          regions: [],
          cities: [],
          lat: null,
          lng: null
        };
        break;
      case 'theme':
        this.filters.themes = [];
        break;
      case 'occasion':
        this.filters.occasions = [];
        break;
      case 'activity':
        this.filters.activities = [];
        break;
      case 'persons':
        this.filters.persons = [];
        break;
      case 'price':
        this.filters.price = { min: null, max: null };
        break;
    }
  }

  /**
   * Clear input states for a specific filter type
   * @param {string} filterType - The type of filter
   */
  clearFilterInputs(filterType) {
    const config = this.filterConfigs[filterType];
    const filterContainer = this.wrapper.querySelector(config.selector);

    if (!filterContainer) return;

    if (filterType === 'price') {
      // Reset price slider to default values
      if (this.minPriceInput) {
        this.minPriceInput.value = this.minPriceInput.getAttribute('data-default') || '0';
        // Trigger input event to update slider display
        this.minPriceInput.dispatchEvent(new Event('input'));
      }
      if (this.maxPriceInput) {
        this.maxPriceInput.value = this.maxPriceInput.getAttribute('data-default') || this.maxPriceInput.max;
        // Trigger input event to update slider display
        this.maxPriceInput.dispatchEvent(new Event('input'));
      }
    } else if (filterType === 'location') {
      // Clear autocomplete input and hidden lat/lng fields
      if (this.locationAutocompleteInput) {
        this.locationAutocompleteInput.value = '';
      }
      if (this.locationLatInput) {
        this.locationLatInput.value = '';
      }
      if (this.locationLngInput) {
        this.locationLngInput.value = '';
      }

      // Hide the around me chip
      if (this.aroundMeChip) {
        this.aroundMeChip.classList.add('hidden');
        this.aroundMeChip.classList.remove('inline-flex');
      }

      // Show the around me button
      if (this.aroundMeButton) {
        this.aroundMeButton.classList.remove('hidden');
      }

      // Uncheck all checkboxes
      const checkboxes = filterContainer.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.checked = false;
      });

      // Reset country and region filtering to show all regions and cities
      this.filterLocationsByCountries();
      this.filterCitiesByRegions();
    } else {
      // Uncheck all checkboxes
      const checkboxes = filterContainer.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.checked = false;
      });
    }
  }

  /**
   * Update filter button label based on active filters
   * @param {string} filterType - The type of filter
   */
  updateFilterButtonLabel(filterType) {
    const config = this.filterConfigs[filterType];
    const button = this.wrapper.querySelector(config.buttonSelector);

    if (!button) return;

    const activeCount = this.getActiveFilterCount(filterType);

    if (activeCount > 0) {
      if (filterType === 'price') {
        // For price, show min/max values instead of count
        const { min, max } = this.filters.price;
        let priceText = config.initialText;

        if (min !== null && max !== null) {
          priceText = `${config.initialText} (${min}€ - ${max}€)`;
        } else if (min !== null) {
          priceText = `${config.initialText} (${min}€+)`;
        } else if (max !== null) {
          priceText = `${config.initialText} (0€ - ${max}€)`;
        }

        button.textContent = priceText;
      } else {
        // For other filters, show count
        button.textContent = `${config.initialText} (${activeCount})`;
      }

      // Add active state to dropdown
      const dropdown = button.closest('.dropdown');
      if (dropdown) {
        dropdown.classList.add('active--filters');
      }
    } else {
      button.textContent = config.initialText;
      // Remove active state from dropdown
      const dropdown = button.closest('.dropdown');
      if (dropdown) {
        dropdown.classList.remove('active--filters');
      }
    }
  }

  /**
   * Get count of active filters for a filter type
   * @param {string} filterType - The type of filter
   * @returns {number} - Count of active filters
   */
  getActiveFilterCount(filterType) {
    switch (filterType) {
      case 'location': {
        let count = this.filters.location.countries.length +
                    this.filters.location.regions.length +
                    this.filters.location.cities.length;

        // Add 1 if autocomplete location is set
        if (this.filters.location.lat !== null && this.filters.location.lng !== null) {
          count += 1;
        }

        return count;
      }
      case 'theme':
        return this.filters.themes.length;
      case 'occasion':
        return this.filters.occasions.length;
      case 'activity':
        return this.filters.activities.length;
      case 'persons':
        return this.filters.persons.length;
      case 'price':
        // Check if price values are different from defaults
        const { min, max } = this.filters.price;
        const minInput = this.minPriceInput;
        const maxInput = this.maxPriceInput;

        if (!minInput || !maxInput) return 0;

        const defaultMin = parseInt(minInput.getAttribute('min')) || 0;
        const defaultMax = parseInt(maxInput.getAttribute('max')) || 0;

        let count = 0;
        if (min !== null && min !== defaultMin) count++;
        if (max !== null && max !== defaultMax) count++;
        return count;
      default:
        return 0;
    }
  }

  /**
   * Close filter dropdown
   * @param {string} filterType - The type of filter
   */
  closeFilterDropdown(filterType) {
    const config = this.filterConfigs[filterType];
    const dropdown = this.wrapper.querySelector(config.selector)?.closest('.dropdown');

    if (dropdown) {
      dropdown.classList.remove('active');
    }
  }

  /**
   * Initialize dropdown-specific event listeners
   */
  initDropdownEventListeners() {
    // Listen for dropdown open/close events if needed
    // This can be extended for specific dropdown behaviors
  }

  /**
   * Initialize sorting event listeners
   */
  initSortingEventListeners() {
    const sortRadios = this.wrapper.querySelectorAll('input[name="sort"]');

    // Initialize sort value from the default checked radio
    const defaultSortRadio = this.wrapper.querySelector('input[name="sort"]:checked');
    if (defaultSortRadio) {
      this.filters.sort = defaultSortRadio.value;
    }

    sortRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          // Update sort filter
          this.filters.sort = radio.value;

          // Update sort button text
          const sortButton = this.wrapper.querySelector('#sort-button span');
          const buttonText = radio.getAttribute('data-text');
          if (sortButton && buttonText) {
            sortButton.textContent = buttonText;
          }

          // Close sort dropdown
          const sortDropdown = radio.closest('.dropdown');
          if (sortDropdown) {
            sortDropdown.classList.remove('active');
          }

          // Sort items client-side instead of triggering full search
          this.sortItems(radio.value);
        }
      });
    });
  }

  /**
   * Initialize mobile drawer functionality
   */
  initMobileDrawer() {
    // Mobile apply filters button
    const mobileApplyBtn = document.getElementById('mobile-apply-filters');
    if (mobileApplyBtn) {
      mobileApplyBtn.addEventListener('click', () => {
        this.handleMobileApply();
      });
    }

    // Mobile clear all filters button
    const mobileClearAllBtn = document.getElementById('mobile-clear-all-filters');
    if (mobileClearAllBtn) {
      mobileClearAllBtn.addEventListener('click', () => {
        this.handleMobileClearAll();
      });
    }
  }

  /**
   * Handle mobile apply filters
   */
  handleMobileApply() {
    // Update all filter values from mobile drawer inputs
    Object.keys(this.filterConfigs).forEach(filterType => {
      if (filterType === 'price') {
        this.updatePriceFilter();
      } else {
        this.updateFilterFromInputs(filterType);
      }
      this.updateFilterButtonLabel(filterType);
    });

    // Update mobile filters button
    this.updateMobileFiltersButton();

    // Trigger search
    this.triggerSearch();
  }

  /**
   * Handle mobile clear all filters
   */
  handleMobileClearAll() {
    // Clear all filter values and inputs
    Object.keys(this.filterConfigs).forEach(filterType => {
      this.clearFilterValues(filterType);
      this.clearFilterInputs(filterType);
      this.updateFilterButtonLabel(filterType);
    });

    // Reset sort to default
    const defaultSortRadio = this.wrapper.querySelector('input[name="sort"]:checked');
    if (defaultSortRadio) {
      this.filters.sort = defaultSortRadio.value;
    } else {
      this.filters.sort = null;
    }

    // Update mobile filters button
    this.updateMobileFiltersButton();

    // Trigger search
    this.triggerSearch();
  }

  /**
   * Get current filters object
   * @returns {Object} - Current filters
   */
  getFilters() {
    return { ...this.filters };
  }

  /**
   * Compare two filter objects for equality
   * @param {Object} filters1 - First filter object
   * @param {Object} filters2 - Second filter object
   * @returns {boolean} - True if filters are equal
   */
  areFiltersEqual(filters1, filters2) {
    // If one is null and the other isn't, they're different
    if (!filters1 || !filters2) {
      return filters1 === filters2;
    }

    // Simple JSON comparison - much cleaner!
    return JSON.stringify(filters1) === JSON.stringify(filters2);
  }

  /**
   * Trigger search with current filters
   */
  triggerSearch() {
    const filters = this.getFilters();

    // Check if filters have changed since last search
    if (this.areFiltersEqual(filters, this.previousFilters)) {
      return; // Skip search if filters haven't changed
    }

    // Store current filters as previous for next comparison
    this.previousFilters = JSON.parse(JSON.stringify(filters));

    // Perform debounced search API call
    this.debouncedPerformSearch(filters);
  }

  /**
   * Perform search API call
   * @param {Object} filters - The filters to send to backend
   */
  performSearch(filters) {
    // Get the API URL from data attribute
    const searchUrl = this.resultsContainer.getAttribute('data-url');
    if (!searchUrl) return;

    // Build URL with filters as query parameters
    const url = this.buildSearchUrl(searchUrl, filters);

    // add the loading state
    this.resultsContainer.classList.add('loading');

    // Send GET request to backend
    fetch(url, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/html'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(html => {
      // Update results HTML
      this.resultsContainer.innerHTML = html;

      // Update results count
      this.updateResultsCount();

      // init offuscation & swiper on the results container
      initOffuscation(this.resultsContainer);
      initSwiper(this.resultsContainer);

      const buttonWrapper = document.getElementById('show-more-experiences-wrapper');
      if (buttonWrapper) {
        buttonWrapper.style.display = '';
        // Reset to page 2 for the next load
        const showMoreBtn = document.getElementById('show-more-experiences');
        if (showMoreBtn) {
          showMoreBtn.setAttribute('data-page', '2');
        }
      }

      // If we had previously loaded more than 1 page of experiences, reload them
      if (this.currentExperiencesPage > 1) {
        this.reloadAdditionalExperiences(filters);
      } else {
        // Reset page counter
        this.currentExperiencesPage = 1;
      }
    })
    .catch(error => {
      console.error('Search error:', error);
    })
    .finally(() => {
      this.resultsContainer.classList.remove('loading');
    });
  }

  /**
   * Update results count display
   */
  updateResultsCount() {
    if (!this.countResultsElts) return;

    // is there a total results count input? because we want the total count from the backend, not the paginated count results
    // if so, get the value from it and remove the input (in case for the next search)
    const totalResultsCountInput = this.wrapper.querySelector('#total-results-count');
    if (totalResultsCountInput) {
      var totalResultsCount = parseInt(totalResultsCountInput.value);
      totalResultsCountInput.remove();
    }
    else{
      // Count items in the results container
      const items = this.resultsContainer.querySelectorAll('[data-gift-package-item], [data-experience-item]');
      var totalResultsCount = items.length;
    }


    // Get appropriate text template based on count (singular vs plural)
    let resultTextTemplate;
    if (totalResultsCount <= 1) {
      resultTextTemplate = this.countResultsElts[0].getAttribute('data-result-text-singular') || '{0} gift idea';
    }
    else {
      resultTextTemplate = this.countResultsElts[0].getAttribute('data-result-text-plural') || '{0} gift ideas';
    }

    // Replace placeholder with actual count
    const resultText = resultTextTemplate.replace('{0}', totalResultsCount);

    // Update the count display
    this.countResultsElts.forEach(elt => {
      elt.textContent = resultText;
      elt.classList.remove('initially-invisible');
    });
  }

  /**
   * Build search URL with filters as query parameters
   * @param {string} baseUrl - Base search URL
   * @param {Object} filters - Filters object
   * @returns {string} Complete URL with query parameters
   */
  buildSearchUrl(baseUrl, filters) {
    const url = new URL(baseUrl, window.location.origin);

    // Add location filters
    if (filters.location.countries.length > 0) {
      filters.location.countries.forEach(id => {
        url.searchParams.append('countries[]', id);
      });
    }

    if (filters.location.regions.length > 0) {
      filters.location.regions.forEach(id => {
        url.searchParams.append('regions[]', id);
      });
    }

    if (filters.location.cities.length > 0) {
      filters.location.cities.forEach(id => {
        url.searchParams.append('cities[]', id);
      });
    }

    // Add lat/lng from autocomplete
    if (filters.location.lat !== null) {
      url.searchParams.append('location_lat', filters.location.lat);
    }

    if (filters.location.lng !== null) {
      url.searchParams.append('location_lng', filters.location.lng);
    }

    // Add theme filters
    if (filters.themes.length > 0) {
      filters.themes.forEach(id => {
        url.searchParams.append('themes[]', id);
      });
    }

    // Add occasion filters
    if (filters.occasions.length > 0) {
      filters.occasions.forEach(id => {
        url.searchParams.append('occasions[]', id);
      });
    }

    // Add activity filters
    if (filters.activities.length > 0) {
      filters.activities.forEach(id => {
        url.searchParams.append('activities[]', id);
      });
    }

    // Add persons filters
    if (filters.persons.length > 0) {
      filters.persons.forEach(count => {
        url.searchParams.append('persons[]', count);
      });
    }

    // Add price filters
    if (filters.price.min !== null) {
      url.searchParams.append('price_min', filters.price.min);
    }

    if (filters.price.max !== null) {
      url.searchParams.append('price_max', filters.price.max);
    }

    // Add sort filter
    if (filters.sort) {
      url.searchParams.append('sort', filters.sort);
    }

    // Add taxonomy ID filter (permanent filter for taxonomy pages)
    if (filters.taxonomyId) {
      url.searchParams.append('taxonomy_id', filters.taxonomyId);
    }

    // Add additional taxonomy ID filter (permanent filter for combined taxonomy pages)
    if (filters.additionalTaxonomyId) {
      url.searchParams.append('additional_taxonomy_id', filters.additionalTaxonomyId);
    }

    return url.toString();
  }

  /**
   * Update mobile filters button with count
   */
  updateMobileFiltersButton() {
    const mobileFiltersBtn = document.getElementById('moreFilters');
    if (!mobileFiltersBtn) return;

    const totalActiveFilters = this.getTotalActiveFiltersCount();
    const baseText = mobileFiltersBtn.getAttribute('data-initial-text') || 'Filters';

    if (totalActiveFilters > 0) {
      mobileFiltersBtn.innerHTML = `${baseText} (${totalActiveFilters}) <i class="icon icon-handle-filters lg:hidden ml-2"></i>`;
      mobileFiltersBtn.classList.add('active--filters');
    } else {
      mobileFiltersBtn.innerHTML = `${baseText} <i class="icon icon-handle-filters lg:hidden ml-2"></i>`;
      mobileFiltersBtn.classList.remove('active--filters');
    }
  }

  /**
   * Get total count of active filters across all types
   * @returns {number} - Total count of active filters
   */
  getTotalActiveFiltersCount() {
    let totalCount = 0;

    Object.keys(this.filterConfigs).forEach(filterType => {
      totalCount += this.getActiveFilterCount(filterType);
    });

    return totalCount;
  }

  /**
   * Initialize show more experiences button
   */
  initShowMoreExperiences() {
    this.showMoreButton = document.getElementById('show-more-experiences');
    if (!this.showMoreButton) return;

    this.showMoreButton.addEventListener('click', () => this.handleShowMoreExperiences());
  }

  /**
   * Handle show more experiences button click
   */
  handleShowMoreExperiences() {
    if (!this.showMoreButton) return;

    const baseUrl = this.showMoreButton.getAttribute('data-url');
    const currentPage = parseInt(this.showMoreButton.getAttribute('data-page'));

    if (!baseUrl || !currentPage) return;

    // Build URL with current filters and page number
    const filters = this.getFilters();
    const url = this.buildSearchUrl(baseUrl, filters);
    const urlWithPage = new URL(url);
    urlWithPage.searchParams.set('page', currentPage);

    // Add loading state to button
    this.showMoreButton.disabled = true;
    this.showMoreButton.classList.add('loading');
    const originalText = this.showMoreButton.textContent;

    // Fetch more experiences
    fetch(urlWithPage.toString(), {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/html'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(html => {
      if (html.trim() === '') {
        // No more experiences to load, hide the button
        if (this.showMoreButton) {
          this.showMoreButton.style.display = 'none';
        }
      }
      else {
        this.resultsContainer.innerHTML += html;

        // Update current page tracker
        this.currentExperiencesPage = currentPage;

        // Update page number for next load
        this.showMoreButton.setAttribute('data-page', currentPage + 1);

        // Initialize offuscation and swiper on new experiences
        initOffuscation(this.resultsContainer);
        initSwiper(this.resultsContainer);
      }
    })
    .catch(error => {
      console.error('Show more experiences error:', error);
    })
    .finally(() => {
      // Remove loading state
      this.showMoreButton.disabled = false;
      this.showMoreButton.classList.remove('loading');
      this.showMoreButton.textContent = originalText;
    });
  }

  /**
   * Reload additional experiences pages after filter change
   * @param {Object} filters - Current filters
   */
  async reloadAdditionalExperiences(filters) {
    const baseUrl = this.resultsContainer.getAttribute('data-url');
    if (!baseUrl) return;

    const maxPage = this.currentExperiencesPage;

    // Load pages 2 through maxPage sequentially
    for (let page = 2; page <= maxPage; page++) {
      try {
        const url = this.buildSearchUrl(baseUrl, filters);
        const urlWithPage = new URL(url);
        urlWithPage.searchParams.set('page', page);

        const response = await fetch(urlWithPage.toString(), {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'text/html'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const html = await response.text();

        if (html.trim() === '') {
          // No more experiences available, update max page and break
          this.currentExperiencesPage = page - 1;
          break;
        }

        // Append the experiences
        this.resultsContainer.innerHTML += html;
      } catch (error) {
        console.error(`Error loading page ${page}:`, error);
        break;
      }
    }

    // Update the button's page attribute to the next page
    const showMoreBtn = document.getElementById('show-more-experiences');
    if (showMoreBtn) {
      showMoreBtn.setAttribute('data-page', this.currentExperiencesPage + 1);
    }

    // Re-initialize offuscation and swiper on all new experiences
    initOffuscation(this.resultsContainer);
    initSwiper(this.resultsContainer);

    // Update results count
    this.updateResultsCount();
  }

  /**
   * Sort items by calling backend to get sorted HTML
   * @param {string} sortType - Sort type value
   */
  sortItems(sortType) {
    // Collect current package and experience IDs from the components
    const packageElements = this.resultsContainer.querySelectorAll('[data-gift-package-item]');
    const experienceElements = this.resultsContainer.querySelectorAll('[data-experience-item]');

    const packageIds = Array.from(packageElements).map(el => parseInt(el.getAttribute('data-package-id')));
    const experienceIds = Array.from(experienceElements).map(el => parseInt(el.getAttribute('data-experience-id')));
    const taxonomyId = this.wrapper.getAttribute('data-taxonomy-id-filter');

    if (packageIds.length === 0 && experienceIds.length === 0) {
      return; // Nothing to sort
    }

    // Get sort URL from data attribute
    const sortButtonWrapper = document.getElementById('sort-button-wrapper');
    const sortUrlBase = sortButtonWrapper.getAttribute('data-sort-url');

    // Add loading state
    this.resultsContainer.classList.add('loading');

    // Build the sort URL with query parameters
    const sortUrl = new URL(sortUrlBase, window.location.origin);
    packageIds.forEach(id => sortUrl.searchParams.append('package_ids[]', id));
    experienceIds.forEach(id => sortUrl.searchParams.append('experience_ids[]', id));
    if (taxonomyId) {
      sortUrl.searchParams.append('taxonomy_id', taxonomyId);
    }

    sortUrl.searchParams.append('sort', sortType);

    // Add nb_adults_selected if present
    const filters = this.getFilters();
    if (filters.persons && filters.persons.length === 1) {
      sortUrl.searchParams.append('nb_adults_selected', filters.persons[0]);
    }

    // Send GET request to backend
    fetch(sortUrl.toString(), {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/html'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(html => {
      // Replace results container HTML with sorted results
      this.resultsContainer.innerHTML = html;

      const buttonWrapper = document.getElementById('show-more-experiences-wrapper');
      if (buttonWrapper) {
        buttonWrapper.style.display = '';
        const showMoreBtn = document.getElementById('show-more-experiences');
        if (showMoreBtn) {
          showMoreBtn.setAttribute('data-page', this.currentExperiencesPage + 1);
        }
      }

      // Re-initialize offuscation and swiper
      initOffuscation(this.resultsContainer);
      initSwiper(this.resultsContainer);

      // Update results count
      // this.updateResultsCount();
    })
    .catch(error => {
      console.error('Sort error:', error);
    })
    .finally(() => {
      this.resultsContainer.classList.remove('loading');
    });
  }
}

export default GiftPackageSearch;
